/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `MainLayout` for the Caintra Connect frontend.
 * @module components/MainLayout
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import { useState, useEffect } from "react";
import { Outlet } from "react-router";
import { useNavigate, useLocation } from "react-router";

import { useNotifications } from "../context/NotificationContext";
import { getToken, refreshToken, signOut } from "../utils/common.js";
import {
  getAuthJsonHeaders,
  isForbiddenResponse,
  TOKEN_EXPIRED_MESSAGE,
} from "../utils/eventApi.js";
import SideMenu from "./SideMenu.jsx";
import MobileHeader from "./MobileHeader.jsx";
import styles from "../styles/SideMenu.module.css";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setNotifications } = useNotifications();

  useEffect(() => {
    if (!getToken()) {
      navigate("/", { replace: true });
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    const getUserNotifications = async () => {
      const token = getToken();
      const userId = localStorage.getItem("userId");
      if (!token || !userId) {
        setNotifications([]);
        return;
      }

      const options = {
        method: "GET",
        headers: getAuthJsonHeaders(),
      };

      try {
        const response = await fetch(
          `${process.env.REACT_APP_ROOT_API}notification/user/${userId}`,
          options,
        );
        const data = await response.json();
        if (data?.message === TOKEN_EXPIRED_MESSAGE) {
          const newTokens = await refreshToken();
          if (!newTokens) {
            setNotifications([]);
            await signOut();
            navigate("/");
            return;
          }
          await getUserNotifications();
          return;
        }
        if (!response.ok || isForbiddenResponse(response, data)) {
          setNotifications([]);
          return;
        }
        setNotifications(data.data || []);
      } catch (error) {
        setNotifications([]);
        logger.error("Error", { err: sanitizeError(error) });
      }
    };

    getUserNotifications();
  }, [location.pathname, navigate, setNotifications]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
      if (window.innerWidth > 767) {
        setIsMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className={styles.mainLayoutContainer}>
      {isMobile && (
        <MobileHeader
          onMenuToggle={toggleMobileMenu}
          isMenuOpen={isMobileMenuOpen}
        />
      )}

      {!isMobile && <SideMenu />}

      {isMobile && (
        <div
          className={`${styles.mobileSideMenu} ${
            isMobileMenuOpen ? styles.open : ""
          }`}
        >
          <SideMenu
            onMobileNavigation={() => setIsMobileMenuOpen(false)}
            isMobile={true}
          />
        </div>
      )}

      <div className={styles.mainContentWithGradient}>
        <Outlet />
      </div>

      {/* Mobile background overlay - always rendered but controlled by CSS */}
      {isMobile && (
        <div
          className={`${styles.mobileBackgroundOverlay} ${
            isMobileMenuOpen ? styles.visible : ""
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
